import { useEffect } from "react";
/**
 * Custom hook to lock/unlock body scroll when a component is active.
 * Useful for modals, drawers, and other overlay components.
 *
 * Uses reference counting so multiple consumers can hold the lock simultaneously.
 * Scroll is only restored when every active consumer has deactivated or unmounted.
 * Inactive consumers (isActive=false) do not modify overflow styles.
 *
 * @param isActive - When true, locks body scroll. When false, does nothing
 * (existing overflow styles are left unchanged for other active consumers).
 *
 * @example
 * ```tsx
 * const MyDrawer = ({ isOpen }) => {
 *   useBodyScrollLock(isOpen);
 *   return <div>...</div>;
 * };
 * ```
 */
// Counts how many consumers currently hold an active scroll lock; overflow only clears when this reaches zero.
let activeScrollLockCount = 0;
export const useBodyScrollLock = (isActive) => {
    useEffect(() => {
        // Inactive consumers must not participate — they should never increment or decrement the shared lock
        if (!isActive)
            return;
        // Hide main page scrollbar when active
        activeScrollLockCount += 1;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        return () => {
            // Cleanup: decrement the lock count when this consumer deactivates or unmounts
            activeScrollLockCount -= 1;
            // Only restore scroll when all consumers have released their lock
            if (activeScrollLockCount === 0) {
                document.body.style.overflow = "";
                document.documentElement.style.overflow = "";
            }
        };
    }, [isActive]);
};
